import React, { useEffect } from 'react';
import {
	LaboratoryRequisitionResultLog,
	LaboratoryRequisitionResultStatus,
	useLaboratoryRequisitionResultLogsQuery,
} from '../../../../graphql/graphql';
import Loading from '../../../../Components/Loading';
import FlatList from '../../../Base/Components/FlatList';
import ScrollContainer from '../../../Base/Components/ScrollContainer';
import { useFlatDataList } from '../../../Base/Hooks/useFlatDataList';
import FormattedMomentDate, {
	DateFormat,
} from '../../../Base/Components/FormattedMomentDate/FormattedMomentDate';
import styles from './IssuedLaboratoryRequestLog.module.scss';
import Text from '../../../../Components/Base/Text/Text';

interface Props {
	requestId: string;
}

const statusMap: Record<LaboratoryRequisitionResultStatus, string> = {
	[LaboratoryRequisitionResultStatus.New]:
		'component.issuedLaboratoryRequestLog.new',
	[LaboratoryRequisitionResultStatus.WaitingForPayment]:
		'component.issuedLaboratoryRequestLog.waitingForPayment',
	[LaboratoryRequisitionResultStatus.Paid]:
		'component.issuedLaboratoryRequestLog.paid',
	[LaboratoryRequisitionResultStatus.PaymentFailure]:
		'component.issuedLaboratoryRequestLog.paymentFailure',
	[LaboratoryRequisitionResultStatus.WaitingForOrder]:
		'component.issuedLaboratoryRequestLog.waitingForOrder',
	[LaboratoryRequisitionResultStatus.XmlCreated]:
		'component.issuedLaboratoryRequestLog.xmlCreated',
	[LaboratoryRequisitionResultStatus.XmlFailure]:
		'component.issuedLaboratoryRequestLog.xmlFailure',
	[LaboratoryRequisitionResultStatus.WaitingForOrderSlot]:
		'component.issuedLaboratoryRequestLog.waitingForOrderSlot',
	[LaboratoryRequisitionResultStatus.OrderSlotReserved]:
		'component.issuedLaboratoryRequestLog.orderSlotReserved',
	[LaboratoryRequisitionResultStatus.OrderSlotCreated]:
		'component.issuedLaboratoryRequestLog.orderSlotCreated',
	[LaboratoryRequisitionResultStatus.OrderSlotFailure]:
		'component.issuedLaboratoryRequestLog.orderSlotFailure',
	[LaboratoryRequisitionResultStatus.Sent]:
		'component.issuedLaboratoryRequestLog.sent',
	[LaboratoryRequisitionResultStatus.SentFailure]:
		'component.issuedLaboratoryRequestLog.sentFailure',
	[LaboratoryRequisitionResultStatus.DocumentCreated]:
		'component.issuedLaboratoryRequestLog.documentCreated',
};

const wrapperId = 'issuedLaboratoryRequestLogWrapper';

const IssuedLaboratoryRequestLog: React.FC<Props> = ({ requestId }) => {
	const { flatData, pagination, getNextPage, appendData, updateItem, reset } =
		useFlatDataList<LaboratoryRequisitionResultLog>();

	const { data, isLoading, refetch, isFetching } =
		useLaboratoryRequisitionResultLogsQuery({
			where: { id: requestId },
			pagination,
		});

	useEffect(() => {
		appendData(
			(data?.laboratoryRequisitionResultLogs
				?.data as LaboratoryRequisitionResultLog[]) ?? [],
		);
	}, [data]);

	if (isLoading) {
		return <Loading />;
	}

	return (
		<ScrollContainer wrapperId={wrapperId} className={styles.wrapper}>
			<FlatList
				onRefresh={refetch}
				onLoadMore={getNextPage}
				data={flatData}
				isLoading={isLoading || isFetching}
				className={styles.list}
				hasMoreData={
					data?.laboratoryRequisitionResultLogs?.meta?.hasNextPage || false
				}
				scrollableTargetId={wrapperId}
				renderItem={(item: LaboratoryRequisitionResultLog) => (
					<div className={styles.item}>
						<FormattedMomentDate
							date={item.createdAt}
							format={DateFormat.DATE_TIME}
						/>
						{item.status && <Text tx={statusMap[item.status]} />}
					</div>
				)}
			/>
		</ScrollContainer>
	);
};

export default IssuedLaboratoryRequestLog;
